'use client';

import * as React from 'react';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import ViewContainer from '../layouts/ViewContainer';
import AppleIcon from '@mui/icons-material/Apple';
import AdbIcon from '@mui/icons-material/Adb';
import WindowIcon from '@mui/icons-material/Window';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';

interface Device {
    id: number;
    name: string; // User-Agent
    model: string | null;
    os: string | null;
    isActive: boolean;
    createdAt: string;
    lastChargedAt: string;
}

interface DevicesViewProps {
    onBack: () => void;
}

const getOsIcon = (osName: string | null) => {
    if (!osName) return <DevicesOtherIcon />;
    const lower = osName.toLowerCase();
    if (lower.includes('win')) return <WindowIcon />;
    if (lower.includes('mac') || lower.includes('ios') || lower.includes('iphone') || lower.includes('ipad')) return <AppleIcon />;
    if (lower.includes('android')) return <AdbIcon />;
    return <DevicesOtherIcon />;
};

export default function DevicesView({ onBack }: DevicesViewProps) {
  const [devices, setDevices] = React.useState<Device[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [deletingId, setDeletingId] = React.useState<number | null>(null);

  const fetchDevices = async () => {
      try {
          const response = await fetch('/devices');
          const data = await response.json();
          
          if (data.success) {
              setDevices(data.devices);
          } else {
              setError(data.error || 'Не удалось загрузить устройства');
          }
      } catch (e) {
          setError('Ошибка сети');
      } finally {
          setLoading(false);
      }
  };

  React.useEffect(() => {
      fetchDevices();
  }, []);

  const handleDelete = async (id: number) => {
      if (!confirm('Вы уверены, что хотите удалить это устройство?')) return;
      
      try {
          setDeletingId(id);
          const response = await fetch(`/devices/${id}/delete`, {
              method: 'POST'
          });
          const data = await response.json();
          
          if (data.success) {
              // Обновляем список
              setDevices(prev => prev.filter(d => d.id !== id));
          } else {
              alert(data.error || 'Ошибка удаления');
          }
      } catch (e) {
          alert('Ошибка сети');
      } finally {
          setDeletingId(null);
      }
  };

  return (
    <ViewContainer title="Мои устройства" onClose={onBack}>
      {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
          </Box>
      ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
      ) : devices.length === 0 ? (
          <Typography align="center" color="text.secondary">У вас пока нет подключенных устройств</Typography>
      ) : (
          <Stack spacing={2}>
              {devices.map((device) => (
                  <Paper
                      key={device.id}
                      elevation={0}
                      sx={{
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2
                      }}
                  >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: '50%', 
                              bgcolor: 'action.hover', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              color: 'text.secondary'
                          }}>
                              {getOsIcon(device.os)}
                          </Box>
                          <Box>
                              <Typography variant="body1" fontWeight="bold">
                                  {device.os || 'Неизвестное устройство'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                  Добавлено: {new Date(device.createdAt).toLocaleDateString()}
                              </Typography>
                          </Box>
                      </Box>
                      
                      <IconButton 
                        aria-label="удалить устройство" 
                        color="error"
                        onClick={() => handleDelete(device.id)}
                        disabled={deletingId === device.id}
                      >
                        {deletingId === device.id ? <CircularProgress size={20} /> : <DeleteIcon />}
                      </IconButton>
                  </Paper>
              ))}
          </Stack>
      )}
    </ViewContainer>
  );
}
